/**
 * <ActivityCopyCamera> — prendre une page de la copie en photo, dans un navigateur (organism).
 *
 * L'écran caméra de l'ancien `CameraComponent.web.js`, sur le design system : le viseur plein écran
 * sur la nuit, le déclencheur rond au bas, la croix en haut ; la photo prise s'affiche en entier, avec
 * « Reprendre » et « Valider ». Au téléphone, la copie passe par le scanner : cet écran ne sert qu'au
 * web.
 *
 * Contrôlé, et sans rien du navigateur : le viseur (`viewfinder`, l'élément vidéo que l'écran pose),
 * l'état de la caméra (`phase`) et la photo arrivent en props ; chaque geste est un rappel. Le viseur
 * reste monté sous la photo prise : « Reprendre » y revient sans attendre la caméra.
 *
 *  - `starting` : la caméra s'ouvre, en mots sur la nuit ;
 *  - `ready` : le viseur, le déclencheur, et le bouton qui retourne la caméra (`onFlip`, au
 *    téléphone et à la tablette) ;
 *  - `captured` : la photo, « Reprendre » et « Valider » ;
 *  - `failed` : ce qui empêche la photo (`failure`), « Réessayer », et « Importer une photo »
 *    (`onImport`) : l'élève rend quand même sa page quand la caméra ne vient pas (Windows, 28/10 : une
 *    caméra tenue par une autre application, et « Réessayer » qui échouait sans fin).
 *
 * Au clavier (Wajih, 21/10) : la caméra prête, Espace ou Entrée prennent la photo, comme le rond
 * (`listenShutterKey`, hors d'un champ où l'on écrit). Sur un appareil qui a un clavier, une ligne
 * sous le rond le dit ; au téléphone, rien.
 */
import React, { useEffect, useRef, useState } from "react"
import { Image, Pressable, View } from "react-native"
import { useSafeAreaInsets } from "react-native-safe-area-context"
import { StyleSheet } from "react-native-unistyles"

import { Button } from "../../atoms/Buttons/Button"
import { IconButton } from "../../atoms/Buttons/IconButton"
import { Stack } from "../../atoms/containers/Stack"
import { Overlay } from "../../atoms/Overlay"
import { Text } from "../../atoms/typography/Text"
import { hasPhysicalKeyboard, listenShutterKey } from "../../utils/shutterKey"

export type ActivityCopyCameraPhase = "starting" | "ready" | "captured" | "failed"
export type ActivityCopyCameraFailure = "denied" | "unavailable" | "busy" | "error"

export type ActivityCopyCameraProps = {
    visible: boolean
    phase: ActivityCopyCameraPhase
    /** Le viseur : la vidéo de la caméra, posée par l'écran. Il remplit la page. */
    viewfinder?: React.ReactNode
    /** La photo prise (`captured`). */
    photo?: string | null
    failure?: ActivityCopyCameraFailure | null
    onShutter: () => void
    onRetake: () => void
    onConfirm: () => void
    onClose: () => void
    onRetry: () => void
    /** Importer une photo à la place, quand la caméra ne s'ouvre pas. Absent : pas de bouton. */
    onImport?: () => void
    /** L'image importée ne s'est pas lue : l'écran le dit, sous les boutons. */
    importFailed?: boolean
    /** Retourner la caméra. Absent : pas de bouton. */
    onFlip?: () => void
    testID?: string
}

export const ACTIVITY_COPY_CAMERA_TEXTS = {
    shutter: "Prendre la photo",
    flip: "Changer de caméra",
    retake: "Reprendre",
    confirm: "Valider",
    retry: "Réessayer",
    import: "Importer une photo",
    importFailed: "Cette image ne s'ouvre pas. Choisis une photo (JPEG ou PNG).",
    starting: "La caméra s'ouvre…",
    shutterKey: "Appuie sur Espace pour prendre la photo",
    failure: {
        denied: "Ton navigateur bloque la caméra. Autorise-la, puis réessaie.",
        unavailable: "On ne trouve pas de caméra sur cet appareil.",
        busy: "Ta caméra est utilisée par une autre application. Ferme-la, puis réessaie.",
        error: "La caméra ne s'est pas ouverte.",
    } satisfies Record<ActivityCopyCameraFailure, string>,
}

/** Le déclencheur : le rond de l'ancien écran, en plus grand que les boutons d'à côté. */
const SHUTTER_SIZE = 72
const SIDE_BUTTON_SIZE = 48

export function ActivityCopyCamera(props: ActivityCopyCameraProps) {
    const {
        visible,
        phase,
        viewfinder,
        photo,
        failure,
        onShutter,
        onRetake,
        onConfirm,
        onClose,
        onRetry,
        onImport,
        importFailed,
        onFlip,
        testID,
    } = props
    const insets = useSafeAreaInsets()
    const captured = phase === "captured" && photo != null
    const ready = visible && phase === "ready"
    const [keyboard] = useState(hasPhysicalKeyboard)
    // Le dernier rappel : l'écoute ne se repose pas à chaque rendu.
    const shutterRef = useRef(onShutter)
    shutterRef.current = onShutter
    useEffect(() => {
        if (!ready) return undefined
        return listenShutterKey(() => shutterRef.current())
    }, [ready])

    return (
        <Overlay visible={visible} onRequestClose={onClose} withCloseButton testID={testID}>
            <View style={styles.page}>
                <View style={styles.viewfinder(captured)}>{viewfinder}</View>
                {captured && <Image source={{ uri: photo }} resizeMode="contain" style={styles.photo} />}

                {/* L'ouverture se dit en mots, sans roue : la règle des chargements du design system. */}
                {phase === "starting" && (
                    <View style={styles.centered} pointerEvents="none">
                        <Text color="text-on-night" center>
                            {ACTIVITY_COPY_CAMERA_TEXTS.starting}
                        </Text>
                    </View>
                )}
                {phase === "failed" && (
                    <View style={styles.centered}>
                        <Stack gap="4" align="center">
                            <Text size="text-lg" weight="semibold" color="text-on-night" center>
                                {ACTIVITY_COPY_CAMERA_TEXTS.failure[failure ?? "error"]}
                            </Text>
                            <Button label={ACTIVITY_COPY_CAMERA_TEXTS.retry} onPress={onRetry} />
                            {onImport != null && (
                                <Button
                                    label={ACTIVITY_COPY_CAMERA_TEXTS.import}
                                    variant="outline"
                                    iconName="file-image"
                                    onPress={onImport}
                                />
                            )}
                            {importFailed && (
                                <Text size="text-sm" color="text-on-night" center>
                                    {ACTIVITY_COPY_CAMERA_TEXTS.importFailed}
                                </Text>
                            )}
                        </Stack>
                    </View>
                )}

                <View style={styles.bar(insets.bottom)}>
                    {captured ? (
                        <View style={styles.choices}>
                            <View style={styles.choice}>
                                <Button
                                    label={ACTIVITY_COPY_CAMERA_TEXTS.retake}
                                    variant="outline"
                                    iconName="redo"
                                    fullWidth
                                    onPress={onRetake}
                                />
                            </View>
                            <View style={styles.choice}>
                                <Button
                                    label={ACTIVITY_COPY_CAMERA_TEXTS.confirm}
                                    iconName="check"
                                    fullWidth
                                    onPress={onConfirm}
                                />
                            </View>
                        </View>
                    ) : (
                        <View style={styles.shutterRow}>
                            <View style={styles.side} />
                            <Pressable
                                onPress={onShutter}
                                disabled={phase !== "ready"}
                                accessibilityRole="button"
                                accessibilityLabel={ACTIVITY_COPY_CAMERA_TEXTS.shutter}
                                accessibilityState={{ disabled: phase !== "ready" }}
                                style={({ pressed }) => styles.shutter(phase === "ready", pressed)}
                            >
                                <View style={styles.shutterCore} />
                            </Pressable>
                            <View style={styles.side}>
                                {onFlip != null && phase === "ready" && (
                                    <IconButton
                                        iconName="sync-alt"
                                        size={SIDE_BUTTON_SIZE}
                                        color="surface-night"
                                        iconColor="text-on-night"
                                        onPress={onFlip}
                                        accessibilityLabel={ACTIVITY_COPY_CAMERA_TEXTS.flip}
                                    />
                                )}
                            </View>
                        </View>
                    )}
                    {keyboard && phase === "ready" && (
                        <Text size="text-sm" color="text-on-night" center style={styles.keyHint}>
                            {ACTIVITY_COPY_CAMERA_TEXTS.shutterKey}
                        </Text>
                    )}
                </View>
            </View>
        </Overlay>
    )
}

const styles = StyleSheet.create((theme) => ({
    page: {
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: theme.colors["surface-night"],
    },
    /** Sous la photo prise, le viseur reste là mais ne se voit plus. */
    viewfinder: (hidden: boolean) => ({
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        opacity: hidden ? 0 : 1,
    }),
    photo: {
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
    },
    centered: {
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        alignItems: "center",
        justifyContent: "center",
        paddingHorizontal: theme.spacing["6"],
    },
    bar: (bottomInset: number) => ({
        position: "absolute",
        left: 0,
        right: 0,
        bottom: 0,
        paddingTop: theme.spacing["4"],
        paddingBottom: bottomInset + theme.spacing["6"],
        paddingHorizontal: theme.spacing["4"],
        alignItems: "center",
        // Le voile de l'ancienne barre : le déclencheur blanc se lit sur une copie blanche.
        backgroundColor: theme.colors.scrim,
    }),
    shutterRow: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        gap: theme.spacing["8"],
    },
    side: { width: SIDE_BUTTON_SIZE, height: SIDE_BUTTON_SIZE, alignItems: "center", justifyContent: "center" },
    shutter: (ready: boolean, pressed: boolean) => ({
        width: SHUTTER_SIZE,
        height: SHUTTER_SIZE,
        borderRadius: SHUTTER_SIZE / 2,
        borderWidth: 4,
        borderColor: theme.colors["text-on-night"],
        alignItems: "center",
        justifyContent: "center",
        opacity: ready ? (pressed ? 0.7 : 1) : 0.4,
    }),
    shutterCore: {
        width: SHUTTER_SIZE - 16,
        height: SHUTTER_SIZE - 16,
        borderRadius: (SHUTTER_SIZE - 16) / 2,
        backgroundColor: theme.colors["text-on-night"],
    },
    /** Les deux choix, côte à côte, centrés : une colonne de téléphone, même sur un grand écran. */
    choices: {
        flexDirection: "row",
        gap: theme.spacing["3"],
        alignSelf: "center",
        maxWidth: 480,
        width: "100%",
    },
    choice: { flex: 1 },
    keyHint: { marginTop: theme.spacing["3"] },
}))
